import { useQuery } from '@tanstack/react-query';
import { supabase } from '../supabase';
import type { BrandKey } from '../../config/brands';
import { brandKeyOf } from './brandFilter';

/**
 * `programs.brand_key` for a set of program ids, for admin reports whose RPC predates the brand
 * column and returns only a program id.
 *
 * A lookup beside the report rather than a change to each report function: the reports are
 * long SECURITY DEFINER bodies, and adding a column to their return type means DROP + CREATE of
 * every one. One indexed read of `programs` by primary key costs less and cannot drift from the
 * column.
 *
 * `data` is undefined until the lookup lands, so a caller can tell "not known yet" from "the
 * default brand" and avoid filing every row under BHCS for a moment.
 */
export function useProgramBrands(programIds: readonly string[]) {
  const ids = [...new Set(programIds)].sort();
  return useQuery({
    queryKey: ['program-brands', ids],
    enabled: ids.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Map<string, BrandKey>> => {
      const { data, error } = await supabase.from('programs').select('id, brand_key').in('id', ids);
      if (error) throw error;
      const rows = (data ?? []) as { id: string; brand_key: string | null }[];
      return new Map(rows.map((r) => [r.id, brandKeyOf(r.brand_key)]));
    },
  });
}
